import { MessageSquareText, Pencil, Plus, Trash2 } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Field,
  Input,
  Textarea,
} from '@aeris/ui'

import {
  MAX_QUICK_REPLIES,
  fillQuickReply,
  useQuickReplies,
  type QuickReply,
} from '../lib/quick-replies.ts'

/**
 * O botão "Atalhos" e o editor dos atalhos da vendedora (F297). As regras
 * (`{nome}`, onde ficam guardados) estão em `lib/quick-replies.ts`.
 */

const newId = (): string =>
  `qr${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`

export const QuickRepliesMenu = ({
  customerName,
  disabled,
  onSend,
}: {
  /** O nome do cliente da conversa, para o `{nome}`. Nulo: só o número. */
  customerName: string | null
  disabled: boolean
  /** Manda na hora para o cliente. */
  onSend: (text: string) => void
}): ReactNode => {
  const { items, save, error } = useQuickReplies()
  const [editing, setEditing] = useState(false)

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            disabled={disabled}
            leadingIcon={
              <MessageSquareText aria-hidden="true" className="h-4 w-4" />
            }
          >
            Atalhos
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-80">
          <DropdownMenuLabel>
            {items.length === 0
              ? 'Você ainda não tem atalhos'
              : 'Clique para mandar ao cliente'}
          </DropdownMenuLabel>
          {items.map((item) => (
            <DropdownMenuItem
              key={item.id}
              onSelect={() => {
                onSend(fillQuickReply(item.text, customerName))
              }}
            >
              <span className="flex min-w-0 flex-col">
                <span className="truncate font-(--weight-medium)">
                  {item.title}
                </span>
                <span className="truncate text-(length:--text-xs) text-(--text-muted)">
                  {fillQuickReply(item.text, customerName)}
                </span>
              </span>
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onSelect={() => {
              setEditing(true)
            }}
          >
            <Pencil aria-hidden="true" className="mr-2 h-4 w-4" />
            {items.length === 0 ? 'Criar atalhos' : 'Editar atalhos'}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {editing ? (
        <QuickRepliesEditor
          initial={items}
          saveError={error}
          onClose={() => {
            setEditing(false)
          }}
          onSave={(next) => {
            save(next)
            setEditing(false)
          }}
        />
      ) : null}
    </>
  )
}

export const QuickRepliesEditor = ({
  initial,
  saveError,
  onClose,
  onSave,
}: {
  initial: readonly QuickReply[]
  saveError: boolean
  onClose: () => void
  onSave: (items: readonly QuickReply[]) => void
}): ReactNode => {
  const [items, setItems] = useState<QuickReply[]>(() =>
    initial.length === 0 ? [{ id: newId(), title: '', text: '' }] : [...initial]
  )

  const update = (id: string, patch: Partial<QuickReply>): void => {
    setItems((current) =>
      current.map((item) => (item.id === id ? { ...item, ...patch } : item))
    )
  }

  // Os vazios não vão; os pela metade seguram o Salvar.
  const filled = items.filter(
    (item) => item.title.trim() !== '' || item.text.trim() !== ''
  )
  const incomplete = filled.some(
    (item) => item.title.trim() === '' || item.text.trim() === ''
  )

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent
        title="Meus atalhos"
        description="Só você vê os seus. Escreva {nome} onde vai o primeiro nome do cliente."
      >
        <div className="flex max-h-[60vh] flex-col gap-4 overflow-y-auto">
          {items.map((item, index) => (
            <div
              key={item.id}
              className="flex flex-col gap-2 border-b border-(--border-subtle) pb-4 last:border-b-0"
            >
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <Field label={`Título do atalho ${String(index + 1)}`}>
                    {(field) => (
                      <Input
                        {...field}
                        value={item.title}
                        maxLength={40}
                        placeholder="Ex.: BOM DIA CLIENTE"
                        onChange={(event) => {
                          update(item.id, { title: event.target.value })
                        }}
                      />
                    )}
                  </Field>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={`Apagar o atalho ${String(index + 1)}`}
                  leadingIcon={<Trash2 aria-hidden="true" className="h-4 w-4" />}
                  onClick={() => {
                    setItems((current) =>
                      current.filter((one) => one.id !== item.id)
                    )
                  }}
                >
                  Apagar
                </Button>
              </div>
              <Field label={`Mensagem do atalho ${String(index + 1)}`}>
                {(field) => (
                  <Textarea
                    {...field}
                    value={item.text}
                    maxLength={1000}
                    rows={3}
                    placeholder="Ex.: Bom dia {nome}, já estamos abertos! Precisando de alguma coisa é só me chamar."
                    onChange={(event) => {
                      update(item.id, { text: event.target.value })
                    }}
                  />
                )}
              </Field>
            </div>
          ))}
          {items.length < MAX_QUICK_REPLIES ? (
            <Button
              variant="secondary"
              size="sm"
              leadingIcon={<Plus aria-hidden="true" className="h-4 w-4" />}
              onClick={() => {
                setItems((current) => [
                  ...current,
                  { id: newId(), title: '', text: '' },
                ])
              }}
            >
              Adicionar atalho
            </Button>
          ) : null}
          {incomplete ? (
            <p className="text-(length:--text-sm) text-(--danger)">
              Todo atalho precisa de título e mensagem.
            </p>
          ) : null}
          {saveError ? (
            <p className="text-(length:--text-sm) text-(--danger)">
              Não foi possível salvar da última vez. Tente de novo.
            </p>
          ) : null}
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            disabled={incomplete}
            onClick={() => {
              onSave(
                filled.map((item) => ({
                  id: item.id,
                  title: item.title.trim(),
                  text: item.text.trim(),
                }))
              )
            }}
          >
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
